import { ChevronRightOutlined, ExpandMoreOutlined } from '@mui/icons-material';
import { Grid as Grid, Typography } from '@mui/material';
import { GridRowParams, GridValidRowModel, useGridApiRef } from '@mui/x-data-grid-pro';
import { ExtendedListItem, ExtendedListItemText, useCurrency } from '@procureai/ui-mui';
import { map } from 'ramda';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Union } from 'ts-toolbelt';

import { NegotiatedQuoteLineItemGridRow } from '../../NegotiatedQuoteWidget.types';
import { TsrctNegotiatedQuote } from '@/generated/gql/graphql';
import { I18nNamespace } from '~/common/types';
import { DataGridProStyled } from '~/components/DataGridCustom';
import { RowDetailPanel } from '~/components/DataGridCustom/components/RowDetailPanel/RowDetailPanel';
import { LabeledDataRow } from '~/components/LabeledDataRow/LabeledDataRow';
import { MaskedValue, MaskedValueVariant } from '~/components/MaskedValue';
import { Multiline } from '~/components/Multiline';
import { PaymentTerms } from '~/components/PaymentTerms/PaymentTerms';
import { useSupplierComment } from '~/hooks';
import { QuoteLineItemGridField } from '~/modules/NegotiationQuoteDetailsWidget/NegotiationQuoteDetailsWidget.types';

import { useNegotiatedQuoteDetailsDataGridConfig } from './NegotiatedQuoteDetails.dataGrid';
import { PreviousPrice } from './NegotiatedQuoteDetails.styled';
import { getRowIdsWithComment } from './NegotiatedQuoteDetails.util';

export interface NegotiatedQuoteDetailsProps {
  isMasked?: boolean;
  data?: Union.Nullable<TsrctNegotiatedQuote>;
  loading?: boolean;
}

export const NegotiatedQuoteDetails = ({ isMasked, data, loading }: NegotiatedQuoteDetailsProps) => {
  const { t } = useTranslation(I18nNamespace.widgets, { keyPrefix: 'NegotiatedQuoteWidget' });
  const apiRef = useGridApiRef();
  const config = useNegotiatedQuoteDetailsDataGridConfig();
  const { format: formatComment } = useSupplierComment();
  const { format: formatCurrency } = useCurrency();

  const items: NegotiatedQuoteLineItemGridRow[] = map((item) => ({ ...item, isMasked }), data?.lineItems ?? []);

  const getDetailPanelContent = useCallback(
    <R extends GridValidRowModel = any>({ row }: GridRowParams<R>) => {
      const lineComment = formatComment(row.supplierComment);

      return (
        <>
          <RowDetailPanel description={row.description} />
          {lineComment ? (
            <Grid
              container
              sx={{
                display: 'flec',
                flexGrow: 1,
              }}
            >
              <Grid size={{ xs: 12, lg: 8, xl: 6 }}>
                <>
                  <ExtendedListItem color="warning" component="div">
                    <ExtendedListItemText
                      secondary={
                        <>
                          <Typography variant="body2">
                            <Multiline>{lineComment}</Multiline>
                          </Typography>
                        </>
                      }
                    />
                  </ExtendedListItem>
                </>
              </Grid>
            </Grid>
          ) : null}
        </>
      );
    },
    [formatComment],
  );

  const getDetailPanelHeight = useCallback(() => 'auto', []);

  return (
    <>
      <Grid
        container
        direction="row"
        spacing={4}
        sx={{
          justifyContent: 'flex-start',
          alignItems: 'stretch',
          alignContent: 'flex-start',
          mb: 6,
        }}
      >
        <Grid container size={{ xs: 12, md: 6 }} spacing={2}>
          <LabeledDataRow loading={loading} label={t('quoteNo')} body={data?.supplierReference} />
          <LabeledDataRow
            loading={loading}
            label={t('totalSum')}
            bodyProps={{
              justifySelf: 'flex-start',
            }}
            body={
              <MaskedValue isMasked={isMasked} variant={MaskedValueVariant.CURRENCY}>
                {formatCurrency(data?.totalDiscountedValue || 0)} (
                <PreviousPrice>{formatCurrency(data?.totalValue || 0)}</PreviousPrice>)
              </MaskedValue>
            }
          />
        </Grid>
        <Grid container size={{ xs: 12, md: 6 }} spacing={2}>
          <LabeledDataRow
            loading={loading}
            label={t('paymentTerms')}
            body={<PaymentTerms items={data?.paymentTerms} />}
          />
        </Grid>
      </Grid>
      {/* No empty init state for detail panel as loading prevents expanding details */}
      {items && items.length ? (
        <DataGridProStyled
          {...config}
          apiRef={apiRef}
          autoHeight
          hideFooter
          disableColumnMenu
          loading={loading}
          rows={items ?? []}
          getDetailPanelHeight={getDetailPanelHeight}
          getDetailPanelContent={getDetailPanelContent}
          slots={{
            detailPanelExpandIcon: ChevronRightOutlined,
            detailPanelCollapseIcon: ExpandMoreOutlined,
          }}
          sx={{
            '& .MuiDataGrid-row': { cursor: 'default' },
          }}
          rowSelection={false}
          initialState={{
            detailPanel: {
              expandedRowIds: getRowIdsWithComment(items),
            },
            pinnedColumns: { right: [QuoteLineItemGridField.STATUS] },
          }}
        />
      ) : null}
    </>
  );
};
